import { isApiError, DEFAULT_ERROR_MESSAGE } from '@/services/api';

// Agreements Slice 3 (#901). A salesperson must never see a snake_case
// error.code. Every refusal from POST /agreements/elemis-applications (3a,
// #900/#918) resolves to one of five actionable groups plus an `unknown`
// catch-all — see docs/plans/2026-08-11-elemis-checkout-journey-branch.md,
// "The refusals — eleven codes, thirteen outcomes, five groups" (now
// fifteen codes / seventeen outcomes).
//
// Branch on error.code, NEVER on error.message — message text is not a
// stable contract.

export type ElemisRefusalGroup =
    | 'configuration'
    | 'journey_state'
    | 'transient'
    | 'refused'
    | 'permission'
    | 'unknown';

export interface ElemisRefusal {
    group: ElemisRefusalGroup;
    /** The published `error.code`, when the response carried one. Absent for
     *  401/403 (no envelope) and for a bare non-ApiError input. */
    code?: string;
    title: string;
    /** The full body text to render in the role="alert" region. Composed per
     *  the group's copy pattern (see the design doc's "Group copy" table). */
    detail: string;
    /** Drives the "Try again" affordance. Pressing it calls `initiate()`
     *  again with NO retry flag (F-D5) — the idempotent replay IS the retry. */
    canRetry: boolean;
}

const GROUP_TITLES: Record<ElemisRefusalGroup, string> = {
    configuration: 'This plan is not set up for payments yet',
    journey_state: 'This quote cannot start a payment',
    transient: 'MotorPlan Payments is not responding',
    refused: 'The payment application was declined',
    permission: 'You do not have permission to take payment',
    unknown: 'Something went wrong',
};

// S7 / group copy table — the permission group renders the CANONICAL string
// verbatim, the same one the shared interceptor toasts for the same 403
// (api.ts) and useCompleteSale.ts renders inline for the dealer-collects
// path, so the two surfaces can never disagree. No backend string is ever
// rendered for this group.
const PERMISSION_MESSAGE = "You don't have permission to perform this action.";
const PERMISSION_REMEDY = 'Ask an administrator to grant your account permission to start the payment journey.';

const CONFIGURATION_REMEDY =
    'An administrator needs to fix this before payment can be taken. Trying again will not help.';

const TRANSIENT_DETAIL =
    'This is temporary. Nothing has been charged and no agreement has been created. Try again in a moment.';

const REFUSED_DETAIL =
    "MotorPlan Payments did not accept this application. Check the customer's details, then try again. If it keeps happening, contact support.";

// journey_state rows each carry their OWN affordance sentence, appended
// after the backend's own message (rows 3/4/5/7/9 of the refusals table).
// paid_in_full_journey's affordance is described here for the alert text
// only — the ACTUAL branch switch (F-D7) is driven by useElemisCheckout
// checking `refusal.code === 'paid_in_full_journey'` directly, not by
// anything read off this map.
const JOURNEY_STATE_AFFORDANCE: Record<string, string> = {
    quote_not_found: 'Return to Agreements.',
    quote_not_live: 'Open the agreement to see its current state.',
    paid_in_full_journey: 'This quote is dealer-collects. Use Complete Quote below to finish the sale.',
    agreement_not_draft: 'Open the agreement to see its current state.',
    // #873. Was 'Review the customer details, then try again.' — already wrong before
    // this change, since this code is also raised for payment_selection.params.amount
    // and .collectionTargets.*, not only the five customer fields. #873's builder
    // precondition (translated to this same code) makes the mismatch worse, not new.
    // Deliberately generic: the exception never carries the field's VALUE, only its
    // name (IncompleteApplicationPayloadException.php:8-9), and the backend's own
    // message already names the field, rendered ahead of this affordance.
    incomplete_application_payload: 'Review the quote details, then try again.',
    // Names no single sibling: this code covers a refusal from any of them,
    // so singling one out would repeat the misattribution it removes.
    upstream_refused:
        'Check the vehicle and customer details on this quote. If they look correct, contact support with the quote reference. Trying again will not help.',
    // The mop-up gate normally intercepts this client-side first; reaching it
    // means client and stored state disagreed, same as Complete Quote's 422.
    vehicle_identity_missing: 'Add a VIN or a registration number for this vehicle, then try again.',
    link_delivery_contact_missing:
        'Add the missing contact to the customer record, or untick that option, then continue to payment again.',
};

// Overrides the shared transient title: the sibling that never answered is
// not MotorPlan Payments. Names no single sibling, as upstream_refused above.
const TRANSIENT_TITLE_OVERRIDES: Partial<Record<string, string>> = {
    upstream_unavailable: 'A required service is not responding',
    security_service_unavailable: 'A required service is not responding',
};

const CONFIGURATION_CODES = new Set(['ambiguous_config_setting', 'missing_elemis_identifier']);

const JOURNEY_STATE_CODES = new Set([
    'quote_not_found',
    'quote_not_live',
    'paid_in_full_journey',
    'agreement_not_draft',
    'incomplete_application_payload',
    'upstream_refused',
    'vehicle_identity_missing',
    'link_delivery_contact_missing',
]);

const REFUSED_CODES = new Set(['elemis_rejected_application']);

// elemis_unavailable appears twice in 3a's table (502 transport/403, 503
// timeout) under the SAME code. elemis_invalid_response and
// upstream_unavailable are their own codes but the same group. 409
// initiation_in_progress (#918, codex review of PR #918/#900) joins them:
// the request is well-formed and would succeed on its own — a concurrent
// initiation holds the per-Agreement lock — so the copy invites an
// immediate retry exactly like a transport hiccup does.
const TRANSIENT_CODES = new Set([
    'elemis_unavailable',
    'elemis_invalid_response',
    'upstream_unavailable',
    'initiation_in_progress',
    'security_service_unavailable',
]);

const DEALER_PRODUCT_NOT_AUTHORISED = 'dealer_product_not_authorised';
const DEALER_PRODUCT_NOT_AUTHORISED_AFFORDANCE = 'Change the dealer or product on the quote.';

function bestEffortMessage(error: unknown): string {
    if (error instanceof Error && error.message) return error.message;
    if (typeof error === 'string' && error) return error;
    return DEFAULT_ERROR_MESSAGE;
}

/** Never throws, for any input. Never returns an empty `detail`. */
export function readElemisRefusal(error: unknown): ElemisRefusal {
    // Not an ApiError at all — a bare Error, a string, undefined, or
    // anything else the mutation's onError could theoretically be called
    // with. Kept structurally distinct from the network-drop case below,
    // which IS an ApiError (see the next check).
    if (!isApiError(error)) {
        return {
            group: 'unknown',
            title: GROUP_TITLES.unknown,
            detail: bestEffortMessage(error),
            canRetry: false,
        };
    }

    // THE COMMONEST TRANSIENT FAILURE ON THE WHOLE JOURNEY, and it MUST be
    // tested before the code switch below. A dropped connection, a DNS
    // failure, a CORS rejection, or a gateway that never answers all leave
    // `error.response` undefined, so `readApiError` returns no `code` and
    // `error.status` is present-but-undefined. Without this row first, such
    // a failure would fall through to `unknown` with NO "Try again" control
    // at the moment of sale.
    if (error.status === undefined) {
        return {
            group: 'transient',
            title: GROUP_TITLES.transient,
            detail: TRANSIENT_DETAIL,
            canRetry: true,
        };
    }

    if (error.status === 403 && error.code === DEALER_PRODUCT_NOT_AUTHORISED) {
        return {
            group: 'journey_state',
            code: error.code,
            title: GROUP_TITLES.journey_state,
            detail: `${error.message} ${DEALER_PRODUCT_NOT_AUTHORISED_AFFORDANCE}`,
            canRetry: false,
        };
    }

    // 403 — no `error.code` at all; the permission middleware does not emit
    // the elemis envelope. The shared interceptor already toasts the
    // canonical string for the same response; this renders it inline too,
    // verbatim, plus one remedy sentence. No backend string, ever (S7).
    if (error.status === 403) {
        return {
            group: 'permission',
            title: GROUP_TITLES.permission,
            detail: `${PERMISSION_MESSAGE} ${PERMISSION_REMEDY}`,
            canRetry: false,
        };
    }

    const { code, message, status } = error;

    if (code && CONFIGURATION_CODES.has(code)) {
        return {
            group: 'configuration',
            code,
            title: GROUP_TITLES.configuration,
            detail: `${message} ${CONFIGURATION_REMEDY}`,
            canRetry: false,
        };
    }

    if (code && JOURNEY_STATE_CODES.has(code)) {
        const affordance = JOURNEY_STATE_AFFORDANCE[code];
        return {
            group: 'journey_state',
            code,
            title: GROUP_TITLES.journey_state,
            detail: affordance ? `${message} ${affordance}` : message,
            canRetry: false,
        };
    }

    if (code && REFUSED_CODES.has(code)) {
        return {
            group: 'refused',
            code,
            title: GROUP_TITLES.refused,
            detail: REFUSED_DETAIL,
            canRetry: true,
        };
    }

    if (code && TRANSIENT_CODES.has(code)) {
        return {
            group: 'transient',
            code,
            title: TRANSIENT_TITLE_OVERRIDES[code] ?? GROUP_TITLES.transient,
            detail: TRANSIENT_DETAIL,
            canRetry: true,
        };
    }

    // Unrecognised code, or no code at all (a bare Laravel validation 422 —
    // "should be unreachable", per the plan, since quote_id is always a uuid
    // from the store). Retry is offered only for a 5xx: an unmapped 4xx is a
    // bug, not something pressing the button again can fix.
    return {
        group: 'unknown',
        code,
        title: GROUP_TITLES.unknown,
        detail: message || DEFAULT_ERROR_MESSAGE,
        canRetry: typeof status === 'number' && status >= 500,
    };
}
